<script>
   /*************************************************************/
   /* AppPlots component for new app                           */
   /* -----------------------------------                       */
   /* defines plot components, controls and parameters          */
   /*************************************************************/

   import { untrack } from 'svelte';
   import { getDefaults } from 'svelte-controls-basic';
   import { Container, Button } from 'svelte-controls-basic';
   import { Vector } from 'mdatools/arrays';
   import { dnorm } from 'mdatools/distributions';

   // global plot router
   import PlotsRouter from '@mdatools-apps/shared/plots/PlotsRouter.svelte';

   // import plot components
   import PlotDiscrimination from '../plots/PlotDiscrimination.svelte';
   import PlotScores from '../plots/PlotScores.svelte';
   import PlotDistance from '../plots/PlotDistance.svelte';

   // import plot controls components
   import { getPlotDiscriminationControls, getPlotScoresControls, getPlotDistanceControls } from '../plots/Controls.svelte.js';

   let {
      res,
      model,
      data,
      markOutlier,
      showContributions
   } = $props();


   const plsres = $derived({'new': res.new.pls});
   const dares = $derived({'new': res.new.da});
   const ncomp = $derived(model.da.ncomp);


   // JSON with plot components (scope is needed for providing correct properties)
   const plots = $derived({
      // objects
      pred: {label: 'Discrimination', scope: 'objects', component: PlotDiscrimination, buttons: buttons1},
      scores: {label: 'Scores', scope: 'objects', component: PlotScores, buttons: buttons1},
      distance: {label: 'Distance', scope: 'objects', component: PlotDistance, buttons: buttons1},

   })

   // the controls are taken once, from the model and the results this component is mounted
   // with, and untrack marks that as deliberate: the tab keys this component on the model and
   // mounts it behind an '{#if}' on the results, which every reset nulls first, so a new model
   // or a new set arrives as a new component; the DA result alone is replaced in place, and no
   // descriptor here reads it.
   // the controls are a plain object: the descriptors are never changed after they are made,
   // so there is nothing here for $state to track
   const controls = untrack(() => ({
      pred: getPlotDiscriminationControls(['new'], model.da.classNames),
      scores: getPlotScoresControls(plsres),
      distance: getPlotDistanceControls(plsres),
   }));

   // which controls do not apply to what the user has currently selected. the factories set
   // 'hidden' for what the data rules out, and Widget combines the two with OR. the outliers
   // zone is drawn under the soft rule only, and the rule is a model setting, not a plot
   // parameter: Widget runs the predicate in a derived while it renders, so the read of the
   // model in it is tracked like a read of the selection
   controls.pred.showOutliers.hiddenWhen = () => model.da.rule === 'hard';

   // JSON with currently selected plots parameters
   let params = $state({
      pred: getDefaults(controls.pred),
      scores: getDefaults(controls.scores),
      distance: getDefaults(controls.distance),
   });


   // combine main elements to be shared with all plots together
   const zscores = Vector.seq(-10.0, 10, 0.1);
   const densnorm = dnorm(zscores);
   const props = $derived({res:plsres, classres:dares, model:model.pls, classmodel: model.da, data,
      ncomp, zscores, densnorm});
</script>

{#snippet buttons1(sel)}
   <Container label="Selected">
      <Button onclick={() => showContributions(sel)} text="plot" />
      <Button onclick={() => markOutlier(sel)} text="remove" class="destructive" />
   </Container>
{/snippet}

<PlotsRouter
   {plots}
   {props}
   {controls}
   bind:params={params}
/>